import { useCallback, useEffect, useRef, useState } from "react";
import {
  AccessibilityInfo,
  View,
  ScrollView,
  StyleSheet,
  Platform,
  Animated,
  LayoutAnimation,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import {
  createNativeStackNavigator,
  type NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { StackActions } from "@react-navigation/native";
import type { OnboardingData, OnboardingStackParamList } from "./types";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import { useOnboarding } from "./OnboardingContext";
import { useOnboardingAnalytics } from "./analytics";
import AnswerChipBar from "./components/AnswerChipBar";
import LivingCellularBackground from "./components/LivingCellularBackground";
import { LouiseMark } from "./components/OnboardingLayout";
import IntroSplashScreen from "./screens/IntroSplashScreen";
import LoginScreen from "./screens/LoginScreen";
import WelcomeGoalsScreen from "./screens/WelcomeGoalsScreen";
import FamilyBuildingPathScreen from "./screens/FamilyBuildingPathScreen";
import HowAreYouHelpingScreen from "./screens/HowAreYouHelpingScreen";
import WorkingWithSurrogateScreen from "./screens/WorkingWithSurrogateScreen";
import SexScreen from "./screens/SexScreen";
import BirthdayScreen from "./screens/BirthdayScreen";
import LocationScreen from "./screens/LocationScreen";
import CreateAccountScreen from "./screens/CreateAccountScreen";
import JourneyStageScreen from "./screens/JourneyStageScreen";
import TreatmentTypeScreen from "./screens/TreatmentTypeScreen";
import PartnerJourneyConfirmScreen from "./screens/PartnerJourneyConfirmScreen";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { getOnboardingViewport } from "./onboardingViewport";

const Stack = createNativeStackNavigator<OnboardingStackParamList>();

type CardMode = "intro" | "stepper" | "custom";

const SCREEN_MODE: Record<keyof OnboardingStackParamList, CardMode> = {
  IntroSplash: "custom",
  Login: "stepper",
  WelcomeGoals: "intro",
  FamilyBuildingPath: "stepper",
  JourneyStage: "stepper",
  TreatmentType: "stepper",
  HowAreYouHelping: "stepper",
  WorkingWithSurrogate: "stepper",
  Sex: "stepper",
  Birthday: "stepper",
  CreateAccount: "stepper",
  Location: "stepper",
  PartnerJourneyConfirm: "stepper",
};

/** Fallback order used before the visitor selects a primary goal. */
const DEFAULT_SCREEN_ORDER: (keyof OnboardingStackParamList)[] = [
  "WelcomeGoals",
  "FamilyBuildingPath",
  "JourneyStage",
  "TreatmentType",
  "HowAreYouHelping",
  "Sex",
  "Birthday",
  "Location",
];

function getScreenOrder(
  primaryIntent: OnboardingData["primaryIntent"],
  fertilityStage: OnboardingData["fertilityStage"],
): (keyof OnboardingStackParamList)[] {
  if (primaryIntent === "grow") {
    const order: (keyof OnboardingStackParamList)[] = [
      "WelcomeGoals",
      "FamilyBuildingPath",
      "Sex",
      "JourneyStage",
    ];
    if (
      fertilityStage === "getting-ready" ||
      fertilityStage === "in-treatment"
    ) {
      order.push("TreatmentType");
    }
    order.push("Birthday", "Location");
    return order;
  }
  if (primaryIntent === "preserve") {
    return ["WelcomeGoals", "JourneyStage", "Sex", "Birthday", "Location"];
  }
  if (primaryIntent === "donate") {
    return ["WelcomeGoals", "HowAreYouHelping", "Birthday", "Location"];
  }
  if (primaryIntent === "learn") {
    return ["WelcomeGoals", "Sex", "Birthday", "Location"];
  }
  return DEFAULT_SCREEN_ORDER;
}

/**
 * When entering onboarding from a partner-invite deep link, skip the
 * primary-patient flow questions (WelcomeGoals, JourneyStage, helper roles)
 * since the partner is supporting an existing journey, not starting one.
 * Their journey is mirrored from the inviter on PartnerJourneyConfirm.
 */
const PARTNER_SCREEN_ORDER: (keyof OnboardingStackParamList)[] = [
  "Sex",
  "Birthday",
  "Location",
];

const ONBOARDING_RIPPLE_ORIGIN = { x: 0.5, y: 0.82 };

function getShortScreenCardHeight(
  route: keyof OnboardingStackParamList,
  compact: boolean,
): number | null {
  switch (route) {
    case "Login":
      return compact ? 580 : 640;
    case "Sex":
      return compact ? 480 : 500;
    case "Birthday":
      return compact ? 390 : 420;
    case "Location":
      return compact ? 390 : 420;
    default:
      return null;
  }
}

function fixtureDisablesMotion() {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_splash_motion") ===
    "off"
  );
}

export default function OnboardingNavigator() {
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  const {
    compact: compactViewport,
    condensed: condensedViewport,
    desktop: desktopViewport,
    mobile: mobileViewport,
  } = getOnboardingViewport(viewportWidth, viewportHeight);
  const onboardingOrbSize = compactViewport ? 72 : condensedViewport ? 84 : 96;
  const {
    data,
    updateField,
    onBackOverride,
    clearPartnerInvite,
    isPartnerInviteFlowActive,
  } = useOnboarding();
  const isPartnerMode = isPartnerInviteFlowActive && !!data.partnerInvite;
  const initialRoute: keyof OnboardingStackParamList = isPartnerMode
    ? "Sex"
    : "IntroSplash";
  const flowStartRoute: keyof OnboardingStackParamList = isPartnerMode
    ? "Sex"
    : "IntroSplash";
  const screenOrder = isPartnerMode
    ? PARTNER_SCREEN_ORDER
    : getScreenOrder(data.primaryIntent, data.fertilityStage);

  // Existing-user partner-invite path: when the invitee is already
  // authenticated and has a Patient row, pre-fill the partner-mode screens
  // from their saved data so the flow feels like confirmation, not re-entry.
  // Only runs when in partner mode + Patient is loaded + onboarding fields
  // are still empty (so user edits aren't overwritten).
  const { data: selfPatient } = usePatient();
  useEffect(() => {
    if (!isPartnerMode || !selfPatient) return;
    if (!data.gender && selfPatient.gender) {
      updateField("gender", selfPatient.gender);
    }
    if (!data.dateOfBirth && selfPatient.dateOfBirth) {
      updateField("dateOfBirth", selfPatient.dateOfBirth);
    }
    const existingZip = selfPatient.addresses?.[0]?.postalCode;
    if (!data.zipCode && existingZip) {
      updateField("zipCode", existingZip);
    }
  }, [
    isPartnerMode,
    selfPatient,
    data.gender,
    data.dateOfBirth,
    data.zipCode,
    updateField,
  ]);

  const [mode, setMode] = useState<CardMode>(
    SCREEN_MODE[initialRoute] ?? "stepper",
  );
  const [currentRoute, setCurrentRoute] =
    useState<keyof OnboardingStackParamList>(initialRoute);
  const [hasInFlowBack, setHasInFlowBack] = useState(false);
  const [motionEnabled, setMotionEnabled] = useState(!fixtureDisablesMotion());
  const [backgroundReady, setBackgroundReady] = useState(Platform.OS !== "web");
  const navRef =
    useRef<NativeStackNavigationProp<OnboardingStackParamList> | null>(null);
  const analytics = useOnboardingAnalytics();

  // Refs let the mount effect read the latest values without listing them
  // in deps (which would re-fire onboarding_started on every change).
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;
  const dataRef = useRef(data);
  dataRef.current = data;
  const hasStartedOnboardingRef = useRef(false);

  // The public splash is intentionally outside the onboarding funnel. Start
  // the funnel only once the visitor reaches the first actual question.
  const initialRouteRef = useRef(initialRoute);
  initialRouteRef.current = initialRoute;
  useEffect(() => {
    if (initialRouteRef.current === "IntroSplash") return;
    hasStartedOnboardingRef.current = true;
    analyticsRef.current.started(dataRef.current);
    analyticsRef.current.stepViewed(initialRouteRef.current, dataRef.current);
  }, []);

  const shimmer = useRef(new Animated.Value(0)).current;

  const screenIndex = screenOrder.indexOf(currentRoute);
  const progress =
    screenIndex >= 0 ? screenIndex / Math.max(screenOrder.length - 1, 1) : 0;

  // Shimmer animation for progress border — continuous sweep
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.delay(300),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  useEffect(() => {
    const fixtureDisabled = fixtureDisablesMotion();
    void AccessibilityInfo.isReduceMotionEnabled().then((reduced) => {
      setMotionEnabled(!fixtureDisabled && !reduced);
    });
    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      (reduced) => setMotionEnabled(!fixtureDisabled && !reduced),
    );
    return () => subscription.remove();
  }, []);

  const handleBackgroundFirstFrame = useCallback(() => {
    setBackgroundReady(true);
  }, []);

  const handleBack = useCallback(() => {
    if (onBackOverride.current) {
      onBackOverride.current();
      return;
    }

    const nav = navRef.current;
    if (!nav) return;

    const state = nav.getState();
    if (state.index > 0) {
      // Pop the screen the user actually visited. This remains correct when
      // screens are inserted, removed, reordered, or reached through a fork.
      nav.goBack();
      return;
    }

    const activeRoute = state.routes[state.index]?.name;
    if (isPartnerMode && activeRoute === flowStartRoute) {
      // Sex is the first screen of an invite flow, but it must never be a
      // dead end. Cancel the invite context and return to the public entry
      // screen so an unauthenticated visitor can reach Login.
      clearPartnerInvite();
      nav.dispatch(StackActions.replace("IntroSplash"));
      return;
    }
    if (activeRoute && activeRoute !== flowStartRoute) {
      // A directly opened URL has no in-flow history to pop. Do not invent a
      // branch the user never visited; return to the stable flow entry.
      nav.dispatch(StackActions.replace(flowStartRoute));
    }
  }, [clearPartnerInvite, flowStartRoute, isPartnerMode, onBackOverride]);

  const handleChipPress = useCallback((screenName: string) => {
    const nav = navRef.current;
    if (!nav) return;
    const state = nav.getState();
    if (!state) return;
    const targetIndex = state.routes.findIndex((r) => r.name === screenName);
    if (targetIndex >= 0 && targetIndex < state.index) {
      // Pop back to the target screen by going back the right number of times
      const popCount = state.index - targetIndex;
      for (let i = 0; i < popCount; i++) {
        nav.goBack();
      }
    }
  }, []);

  const handleStateChange = useCallback(
    (state: { routes: { name: string }[]; index: number } | undefined) => {
      if (!state) return;
      const route = state.routes[state.index];
      if (!route) return;
      const screenName = route.name as keyof OnboardingStackParamList;

      const isPreFunnelRoute =
        screenName === "IntroSplash" || screenName === "Login";

      if (!isPreFunnelRoute && !hasStartedOnboardingRef.current) {
        hasStartedOnboardingRef.current = true;
        analyticsRef.current.started(dataRef.current);
      }

      // Fire onboarding_step_viewed on each onboarding route change. The
      // splash is a pre-funnel brand surface and is not counted as a step.
      if (!isPreFunnelRoute && screenName !== initialRouteRef.current) {
        analyticsRef.current.stepViewed(screenName, dataRef.current);
      }
      setCurrentRoute(screenName);
      setHasInFlowBack(state.index > 0);
      const newMode = SCREEN_MODE[screenName] ?? "stepper";

      setMode((prev) => {
        if (prev === newMode) return prev;

        // Animate card layout change on native
        if (Platform.OS !== "web") {
          LayoutAnimation.configureNext(
            LayoutAnimation.create(
              400,
              LayoutAnimation.Types.easeInEaseOut,
              LayoutAnimation.Properties.opacity,
            ),
          );
        }

        return newMode;
      });
    },
    [],
  );

  const isCustom = mode === "custom";
  const isIntro = mode === "intro";
  const dockCondensedCard = !isCustom && condensedViewport;
  const dockMobileCard = !isCustom && mobileViewport;
  const desktopCard = !isCustom && desktopViewport;
  const isIOS = Platform.OS === "ios";
  // Sparse form steps should end shortly after their final control. The
  // journey and account screens retain the taller card because their content
  // can grow or change phase.
  const shortScreenCardHeight = isCustom
    ? null
    : getShortScreenCardHeight(currentRoute, compactViewport);
  const iosAccountCardHeight =
    isIOS && currentRoute === "CreateAccount"
      ? compactViewport
        ? 560
        : 520
      : null;
  const inFlowLoginHeaderHeight =
    currentRoute === "Login" && hasInFlowBack ? (compactViewport ? 44 : 48) : 0;
  // Condensed viewports still need an explicit height for the taller routes so
  // the card remains stable while navigation content mounts.
  const condensedCardHeight = Math.min(isIntro ? 500 : 560, viewportHeight);
  const fixedCardHeight =
    shortScreenCardHeight !== null
      ? shortScreenCardHeight + inFlowLoginHeaderHeight
      : iosAccountCardHeight !== null
        ? iosAccountCardHeight
        : dockCondensedCard
          ? condensedCardHeight
          : null;

  const card = (
    <View
      testID="onboarding-glass-card"
      style={[
        !isCustom && styles.glassCard,
        isIOS && styles.glassCardIOS,
        isIntro
          ? styles.cardIntro
          : isCustom
            ? styles.cardCustom
            : styles.cardStepper,
        fixedCardHeight !== null && styles.cardFixedHeight,
        dockCondensedCard && styles.cardCondensed,
        dockMobileCard && styles.cardDocked,
        desktopCard && styles.cardDesktop,
        fixedCardHeight !== null && {
          height: Math.min(fixedCardHeight, viewportHeight),
        },
        !isCustom &&
          !isIntro &&
          viewportHeight < 680 &&
          styles.cardStepperCompact,
        !isCustom &&
          Platform.OS === "web" && {
            transition:
              "border-radius 400ms ease, height 400ms ease, margin 400ms ease, max-width 400ms ease, max-height 400ms ease, padding 400ms ease",
          },
      ]}
    >
      {!isCustom &&
        (isIOS ? (
          <>
            <BlurView
              pointerEvents="none"
              intensity={32}
              tint="dark"
              experimentalBlurMethod="dimezisBlurView"
              style={[
                StyleSheet.absoluteFill,
                styles.glassEffect,
                desktopCard && styles.glassEffectDesktop,
                (dockCondensedCard || dockMobileCard) &&
                  styles.glassEffectDocked,
              ]}
            />
            <View
              pointerEvents="none"
              style={[
                StyleSheet.absoluteFill,
                styles.glassEffect,
                styles.glassWarmthIOS,
                desktopCard && styles.glassEffectDesktop,
                (dockCondensedCard || dockMobileCard) &&
                  styles.glassEffectDocked,
              ]}
            />
          </>
        ) : (
          <>
            <View
              key={backgroundReady ? "background-ready" : "background-loading"}
              pointerEvents="none"
              style={[
                styles.glassBackdrop,
                Platform.OS === "web" && styles.glassCardWeb,
              ]}
            />
            <View pointerEvents="none" style={styles.glassWash} />
          </>
        ))}

      {/* Progress gradient border at top of card */}
      {!isCustom && progress > 0 && (
        <View
          style={{
            position: "absolute",
            top: 34,
            left: "50%",
            width: 120,
            height: 2,
            marginLeft: -60,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor: "rgba(255, 255, 255, 0.14)",
            zIndex: 20,
          }}
        >
          <View style={{ width: `${progress * 100}%`, height: 2 }}>
            <LinearGradient
              colors={["#B84A16", "#FDDAB0", "#B08030"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ flex: 1 }}
            />
            {/* Shimmer overlay */}
            <Animated.View
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: 24,
                transform: [
                  {
                    translateX: shimmer.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-24, 120],
                    }),
                  },
                ],
              }}
            >
              <LinearGradient
                colors={[
                  "transparent",
                  "rgba(255, 255, 255, 0.6)",
                  "transparent",
                ]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{ flex: 1 }}
              />
            </Animated.View>
          </View>
        </View>
      )}

      {/* Answer chip bar — persists across screen transitions */}
      {!isCustom &&
        currentRoute !== "CreateAccount" &&
        currentRoute !== "PartnerJourneyConfirm" &&
        (currentRoute !== "Login" || hasInFlowBack) && (
          <AnswerChipBar
            mode={isIntro ? "intro" : "stepper"}
            canGoBack={
              currentRoute !== "IntroSplash" &&
              (currentRoute !== flowStartRoute || isPartnerMode)
            }
            onBack={handleBack}
            onChipPress={handleChipPress}
            currentRoute={currentRoute}
          />
        )}

      {/* Navigator renders screen content inside the card */}
      <View
        style={{
          flex: 1,
          borderTopLeftRadius: isCustom ? 0 : 28,
          borderTopRightRadius: isCustom ? 0 : 28,
          borderBottomLeftRadius: isCustom || dockMobileCard ? 0 : 28,
          borderBottomRightRadius: isCustom || dockMobileCard ? 0 : 28,
          overflow: "hidden",
        }}
      >
        <Stack.Navigator
          initialRouteName={initialRoute}
          screenOptions={{
            headerShown: false,
            contentStyle: { backgroundColor: "transparent" },
            animation: "fade",
            animationDuration: 180,
          }}
          screenListeners={({ navigation }) => {
            const syncNavigationState = (
              state:
                | {
                    routes: { name: string }[];
                    index: number;
                  }
                | undefined,
            ) => {
              navRef.current =
                navigation as NativeStackNavigationProp<OnboardingStackParamList>;
              handleStateChange(state);
            };

            // Capture nav ref immediately, not just on state changes
            navRef.current =
              navigation as NativeStackNavigationProp<OnboardingStackParamList>;
            return {
              focus: () =>
                syncNavigationState(
                  navigation.getState() as
                    | {
                        routes: { name: string }[];
                        index: number;
                      }
                    | undefined,
                ),
              state: (e) =>
                syncNavigationState(
                  e.data.state as
                    | {
                        routes: { name: string }[];
                        index: number;
                      }
                    | undefined,
                ),
            };
          }}
        >
          <Stack.Screen name="IntroSplash" component={IntroSplashScreen} />
          <Stack.Screen name="Login" component={LoginScreen} />
          <Stack.Screen name="WelcomeGoals" component={WelcomeGoalsScreen} />
          <Stack.Screen
            name="FamilyBuildingPath"
            component={FamilyBuildingPathScreen}
          />
          <Stack.Screen name="JourneyStage" component={JourneyStageScreen} />
          <Stack.Screen name="TreatmentType" component={TreatmentTypeScreen} />
          <Stack.Screen
            name="HowAreYouHelping"
            component={HowAreYouHelpingScreen}
          />
          <Stack.Screen
            name="WorkingWithSurrogate"
            component={WorkingWithSurrogateScreen}
          />
          <Stack.Screen name="Sex" component={SexScreen} />
          <Stack.Screen name="Birthday" component={BirthdayScreen} />
          <Stack.Screen name="Location" component={LocationScreen} />
          <Stack.Screen name="CreateAccount" component={CreateAccountScreen} />
          <Stack.Screen
            name="PartnerJourneyConfirm"
            component={PartnerJourneyConfirmScreen}
          />
        </Stack.Navigator>
      </View>
    </View>
  );

  return (
    <View style={styles.root}>
      {!isCustom && (
        <>
          <LivingCellularBackground
            motionEnabled={motionEnabled}
            onFirstFrame={handleBackgroundFirstFrame}
            origin={ONBOARDING_RIPPLE_ORIGIN}
          />
          <LinearGradient
            pointerEvents="none"
            colors={[
              "rgba(21, 8, 2, 0.08)",
              "rgba(21, 8, 2, 0.00)",
              "rgba(14, 6, 2, 0.46)",
            ]}
            locations={[0, 0.46, 1]}
            style={StyleSheet.absoluteFill}
          />
        </>
      )}

      {/* SafeAreaView with no edges when Login owns the layout */}
      <SafeAreaView
        style={{ flex: 1 }}
        edges={isCustom ? [] : ["top", "left", "right"]}
      >
        <ScrollView
          contentContainerStyle={
            isCustom
              ? styles.wrapperCustom
              : [
                  styles.wrapperDefault,
                  viewportHeight < 680 && styles.wrapperCompact,
                  condensedViewport && styles.wrapperCondensed,
                  dockMobileCard && styles.wrapperDocked,
                  desktopCard && styles.wrapperDesktop,
                ]
          }
          bounces={false}
          scrollEnabled
          showsVerticalScrollIndicator={false}
        >
          {(currentRoute === "WelcomeGoals" || currentRoute === "Login") && (
            <View
              testID={
                currentRoute === "Login" ? "onboarding-login-orb" : undefined
              }
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              pointerEvents="none"
              style={[
                styles.onboardingOrb,
                { marginBottom: -onboardingOrbSize / 2 },
              ]}
            >
              <View style={styles.orbLogoLayer} pointerEvents="none">
                <LouiseMark size={onboardingOrbSize * 0.4} />
              </View>
              <View style={styles.orbVisualLayer}>
                <GradientOrb size={onboardingOrbSize} />
              </View>
            </View>
          )}
          {card}
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  glassCard: {
    borderTopLeftRadius: 260,
    borderTopRightRadius: 260,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
    overflow: "hidden",
    backgroundColor: "rgba(16, 11, 8, 0.30)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.12)",
    shadowColor: "#130702",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.28,
    shadowRadius: 34,
  },
  glassCardWeb: {
    backdropFilter: "saturate(1.35) blur(14px)",
    WebkitBackdropFilter: "saturate(1.35) blur(14px)",
  },
  glassCardIOS: {
    backgroundColor: "rgba(16, 11, 8, 0.22)",
    borderColor: "transparent",
  },
  glassWarmthIOS: {
    backgroundColor: "rgba(112, 44, 14, 0.12)",
  },
  glassEffect: {
    borderTopLeftRadius: 72,
    borderTopRightRadius: 72,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  glassEffectDesktop: {
    borderRadius: 28,
  },
  glassEffectDocked: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  glassBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  glassWash: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(42, 32, 26, 0.12)",
  },
  cardIntro: {
    flex: 1,
    maxHeight: 700,
    minHeight: 520,
    width: "100%",
    maxWidth: 520,
    alignSelf: "center",
    borderTopLeftRadius: 72,
    borderTopRightRadius: 72,
    paddingTop: 22,
  },
  cardStepper: {
    flex: 1,
    maxHeight: 700,
    minHeight: 500,
    width: "100%",
    maxWidth: 520,
    alignSelf: "center",
    borderTopLeftRadius: 72,
    borderTopRightRadius: 72,
    paddingTop: 34,
    paddingBottom: 16,
  },
  cardStepperCompact: {
    paddingTop: 30,
    paddingBottom: 4,
  },
  cardFixedHeight: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: "auto",
    minHeight: 0,
  },
  cardCondensed: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  cardDocked: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  cardDesktop: {
    borderRadius: 28,
  },
  cardCustom: {
    flex: 1,
    width: "100%",
  },
  wrapperDefault: {
    flexGrow: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 104,
    paddingBottom: 12,
  },
  wrapperCustom: {
    flexGrow: 1,
  },
  wrapperCompact: {
    paddingHorizontal: 8,
    paddingTop: 0,
    paddingBottom: 4,
  },
  wrapperCondensed: {
    paddingBottom: 0,
  },
  wrapperDocked: {
    paddingBottom: 0,
  },
  wrapperDesktop: {
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 32,
  },
  onboardingOrb: {
    alignItems: "center",
    position: "relative",
    zIndex: 30,
  },
  orbLogoLayer: {
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 0,
  },
  orbVisualLayer: {
    position: "relative",
    zIndex: 1,
  },
});
